import { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import { Typography, TableHeadCell, SimpleTable } from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { useSort, useSaleStatsQuery } from '../../hooks';
import { i18n } from '../../i18n';

interface SaleStatsPageProps {
  innerScroll?: boolean;
}

export const SaleStatsPage = styled.div<SaleStatsPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const SaleStatsHeader = ({ totalCount }: any) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.saleStats.title', { totalCount })}
      </Typography>
    </DashboardHeader>
  );
};

export const SaleStatsBody = ({ setTotalCount }: any) => {
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: 'containerForSaleCount',
    sortDirection: 'desc',
  });

  const { data = { data: [], totalCount: 0 }, isFetching } = useSaleStatsQuery();

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const sortedData = useMemo(() => {
    return {
      data: [...data.data].sort((a: any, b: any) =>
        sortDirection === 'desc' ? b[sortField] - a[sortField] : a[sortField] - b[sortField],
      ),
      totalCount: data.totalCount,
    };
  }, [data.data, sortField, sortDirection]);

  return (
    <SimpleTable
      data={sortedData}
      tableRowProps={(stats, i) => ({
        key: stats.client.code + i,
        onClick: () => {},
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('common.client'),
          body: (stats) => (
            <TableCell component={'th'} align={'left'}>
              {stats.client.code}
            </TableCell>
          ),
        },
        {
          column: 1,
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'containerForSaleCount'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.forSaleCount')}
            </TableHeadCell>
          ),
          bodyText: (stats) => stats.containerForSaleCount,
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'containerSoldCount'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.soldCount')}
            </TableHeadCell>
          ),
          bodyText: (stats) => stats.containerSoldCount,
        },
      ]}
    />
  );
};
